import { useSyncExternalStore } from 'react';

/**
 * SIL-364: pinned-nav-item state for the pillar-rail nav. Client-only,
 * localStorage-persisted (same pattern as the existing `siliconx-theme`
 * key) — no server persistence. A route path (e.g. `/assets`) is the pin
 * identity; nav item metadata (label/icon/pillar) is resolved from
 * CUSTOMER_NAV at render time, never stored here.
 *
 * Lives in shared/ui (not the customer app) so both the nav rail's
 * "Pinned" panel and each page's per-header Pin/Pinned toggle read/write
 * the same store and stay in sync within a tab (custom event) and across
 * tabs (native `storage` event) without a new state-management dependency.
 */

const STORAGE_KEY = 'siliconx-nav-pins';
const CHANGE_EVENT = 'siliconx-nav-pins-changed';

function readRaw(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function writeRaw(paths: string[]) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(paths));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function getPinnedPaths(): string[] {
  return readRaw();
}

export function isPathPinned(to: string): boolean {
  return readRaw().includes(to);
}

export function togglePinnedPath(to: string) {
  const current = readRaw();
  const next = current.includes(to) ? current.filter((p) => p !== to) : [...current, to];
  writeRaw(next);
}

function subscribe(onChange: () => void) {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

// Cache the last-read array reference so useSyncExternalStore's snapshot
// only changes identity when the underlying JSON actually changes —
// required to avoid an infinite re-render loop.
let cachedRaw: string | null = null;
let cachedSnapshot: string[] = [];

function getSnapshot(): string[] {
  const raw = typeof window === 'undefined' ? null : window.localStorage.getItem(STORAGE_KEY);
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedSnapshot = readRaw();
  }
  return cachedSnapshot;
}

function getServerSnapshot(): string[] {
  return [];
}

/** Live-subscribed list of pinned route paths. */
export function usePinnedPaths(): string[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
